import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import type { MeResponse, Role } from '@gn/shared';
import { api, onUnauthenticated, ApiError } from './api';
import { LoadingState } from '@/components/states';

export const ME_KEY = ['me'] as const;

const LAST_ME = 'gn.lastMe';

/** Last known identity (no secrets) so the PWA can open offline; always re-validated when online. */
function readLastMe(): MeResponse | undefined {
  try {
    const v = localStorage.getItem(LAST_ME);
    return v ? (JSON.parse(v) as MeResponse) : undefined;
  } catch {
    return undefined;
  }
}

export function forgetLastMe() {
  try {
    localStorage.removeItem(LAST_ME);
  } catch {
    /* ignore */
  }
}

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const me = await api<MeResponse>('/auth/me', { quiet401: true });
        try {
          localStorage.setItem(LAST_ME, JSON.stringify(me));
        } catch {
          /* storage unavailable */
        }
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) forgetLastMe();
        throw e;
      }
    },
    initialData: readLastMe,
    initialDataUpdatedAt: 0,
    retry: (n, e) => !(e instanceof ApiError && (e.status === 401 || e.isNetwork)) && n < 2,
    staleTime: 60_000,
  });
}

/** Signed in = we have an identity and the server has not rejected the session. */
export function isSignedIn(me: { data?: MeResponse; error: unknown }): boolean {
  return Boolean(me.data) && !(me.error instanceof ApiError && me.error.status === 401);
}

export function useHasRole(role: Role): boolean {
  const me = useMe();
  return Boolean(me.data?.user.roles.includes(role));
}

/** Clears the cached identity whenever any request returns 401 (expired/revoked session). */
export function AuthWatcher() {
  const qc = useQueryClient();
  React.useEffect(() => {
    const off = onUnauthenticated(() => {
      forgetLastMe();
      qc.setQueryData(ME_KEY, undefined);
      void qc.invalidateQueries({ queryKey: ME_KEY });
    });
    return () => {
      off();
    };
  }, [qc]);
  return null;
}

export function RequireAuth({ children, role }: { children: React.ReactNode; role?: Role }) {
  const me = useMe();
  const loc = useLocation();
  if (me.isLoading) return <div className="p-8"><LoadingState /></div>;
  if (me.error instanceof ApiError && me.error.isNetwork && !me.data) {
    // Offline with no cached identity: allow local drafts but show the app; API calls will fail gracefully.
    return <>{children}</>;
  }
  if (!isSignedIn(me)) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (role && !me.data!.user.roles.includes(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}
